import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { ServiceUri } from "@common/configuration/service-uri";
import { IMcpOAuthAuthorization } from "@common/identity/mcp-oauth-authorization.interface";
import { IUserDetailBindingModel } from "@common/identity/user-detail-binding-model.interface";
import { lastValueFrom } from "rxjs";

export interface IEndedRole {
    RoleId: number;
    OrganisationId: number;
    Label: string;
}

export interface IPersonAspNetRole {
    PersonId: number;
    PersonName: string;
    RoleName: string;
}

export interface IImpactTask {
    label: string;
    taskId: number;
}

export interface IAccessToken {
    AccessToken: string;
}

export interface IRegisterOrganisationBindingModel {
    OrganisationName: string;
    UrlIdentifier: string;
}

export interface IAddCoach {
    PersonId?: number;
    PartnerId?: number;
    UserDetails?: IUserDetailBindingModel;
    NewOrganisation?: IRegisterOrganisationBindingModel;
    OrganisationId?: number;
    CoachRoleTypeCodes?: RoleTypeCode[];
    StartDate: Date;
    EndDate?: Date;
    Notes?: string;
}

export interface IAddCoachResponse {
    PersonId: number;
    PractitionerId: number;
    orgIds: number[];
}

export interface IRoleMapping {
    SourceRoleId: number;
    DestinationRoleId?: number;
    DestinationRoleName?: string;
}

export interface ICopySystem {
    SystemId: number;
    DestinationOrganisationId: number;
    DestinationSystemName?: string;
    RoleIdMapping: IRoleMapping[];
}

export interface ISystemRole {
    Label: string;
    RoleId: number;
}

export interface ICopyTeam {
    TeamId: number;
    DestinationOrganisationId: number;
    DestinationTeamName?: string;
    ExtendedCopy?: boolean;
    RoleIdMapping: IRoleMapping[];
}

export interface ICacheStatus {
    Name: string;
    Values: { Id: string; Value: any }[];
}

export interface ICopyMeetingOrTemplate {
    MeetingId?: number;
    MeetingAgendaTemplateId?: number,
    DestinationOrganisationId: number,
    DestinationTeamId?: number,
    DestinationTypeIsMeeting: boolean;
}

export interface IExportLeaders {
    // The type of people to include in the export
    IncludeTeamLeaders: boolean;
    IncludeOrgLeaders: boolean;
    IncludeFunctionLeaders: boolean;
    IncludeCulturalLeaders: boolean;
    IncludeOwners: boolean;
    // The types of organisations to include in the export
    IncludeStrategicPartnerCoachedOrganisations: boolean;
}

export interface IMcpOAuthClient {
    ClientId: string;
    DisplayName: string;
    RedirectUris: string[];
    IsConfigurationManaged: boolean;
    IsClientMetadataDomain: boolean;
    IsApproved: boolean;
    IsConfidential: boolean;
    ClientSecret?: string;
}

export interface IMcpOAuthClientUpdate {
    generateClientSecret?: boolean;
    displayName: string;
    redirectUris: string[];
}

@Injectable()
export class StakeholderServicesService {
    public static readonly Id = "ADAPT.Nimbus.StakeholderServices.Service";

    public seedDatabase = this.generateServiceCallFunction("SeedDatabase", "text");
    public seedTrainingOrgs = this.generateServiceCallFunction("SeedTrainingOrganisations", "text");
    public deleteOrganisation = this.generateServiceCallFunction("DeleteOrganisation");
    public getOrphanUsers = this.generateServiceCallFunction("GetOrphanUsers");
    public getOrphanPeople = this.generateServiceCallFunction("GetOrphanPeople");
    public deleteOrphanUsers = this.generateServiceCallFunction("DeleteOrphanUsers");
    public deleteOrphanPeople = this.generateServiceCallFunction("DeleteOrphanPeople");
    public deleteSurvey = this.generateServiceCallFunction("DeleteSurvey");
    public mergeUsers = this.generateServiceCallFunction("MergeUsers");
    public deleteUser = this.generateServiceCallFunction("DeleteUser");
    public updateLogInEmailAddress = this.generateServiceCallFunction("UpdateLogInEmailAddress");
    public archiveOrganisation = this.generateServiceCallFunction("ArchiveOrganisation");
    public cancelSubscription = this.generateServiceCallFunction("CancelSubscription");
    public resumeTrial = this.generateServiceCallFunction("ResumeTrial");
    public extendTrial = this.generateServiceCallFunction("ExtendTrial");
    public detachPaymentMethod = this.generateServiceCallFunction("DetachPaymentMethod");
    public getCacheStatus = this.generateServiceCallFunction<ICacheStatus[]>("GetCacheStatus");
    public clearCache = this.generateServiceCallFunction("ClearCache");
    public clearCacheByName = this.generateServiceCallFunction("ClearCacheByName");
    public getSpeedCatchupStats = this.generateServiceCallFunction("GetSpeedCatchupStats");
    public resetPassword = this.generateServiceCallFunction("ResetPassword");
    public loginAs = this.generateServiceCallFunction<IAccessToken>("LoginAs");
    public getPrivilegedUserRoles = this.generateServiceCallFunctionWithUnwrap<IPersonAspNetRole[]>("GetPrivilegedUserRoles");
    public getActivationLink = this.generateServiceCallFunction<string>("GetActivationLink");
    public getProcessMapRolesFromSystem = this.generateServiceCallFunction<ISystemRole[]>("GetProcessMapRolesFromSystem");
    private generateTableSasCall = this.generateServiceCallFunction<string>("GenerateTableSas");
    public getCoachFeedbackLink = this.generateServiceCallFunction<string>("GetCoachFeedbackLink");
    public generatePathwayRatingAuditTableSas = this.generateServiceCallFunction<string>("GeneratePathwayRatingAuditTableSas");
    public resetTwoFactor = this.generateServiceCallFunction<string>("ResetTwoFactor");

    public constructor(
        private httpClient: HttpClient,
    ) {
    }

    public createCoach(params: IAddCoach) {
        const url = this.generateUrl("CreateCoach");
        return this.httpClient.post<IAddCoachResponse>(url, params, { responseType: "json" });
    }

    public copySystem(params: ICopySystem) {
        const url = this.generateUrl("CopySystem");
        return this.httpClient.post<number>(url, params, { responseType: "json" });
    }

    public copyTeam(params: ICopyTeam) {
        const url = this.generateUrl("CopyTeam");
        return this.httpClient.post<number>(url, params, { responseType: "json" });
    }

    public copyMeetingOrTemplate(params: ICopyMeetingOrTemplate) {
        const url = this.generateUrl("CopyMeetingOrTemplate");
        return this.httpClient.post<number>(url, params, { responseType: "json" });
    }

    public exportLeadersOwners(params: IExportLeaders) {
        const url = this.generateUrl("ExportLeadersOwners");
        return this.httpClient.post(url, params, {
            responseType: "blob",
            observe: "response",
        });
    }

    public getMcpOAuthClients() {
        return lastValueFrom(this.httpClient.get<IMcpOAuthClient[]>(this.generateUrl("McpOAuthClients")));
    }

    public getMcpOAuthClientSecret(clientId: string) {
        const url = `${this.generateUrl("McpOAuthClients")}/${encodeURIComponent(clientId)}/ClientSecret`;
        return lastValueFrom(this.httpClient.get<{ ClientSecret: string | null }>(url));
    }

    public putMcpOAuthClient(clientId: string, client: IMcpOAuthClientUpdate) {
        const url = `${this.generateUrl("McpOAuthClients")}/${encodeURIComponent(clientId)}`;
        return lastValueFrom(this.httpClient.put<IMcpOAuthClient>(url, client));
    }

    public approveMcpOAuthClientMetadataDomain(domain: string) {
        const url = `${this.generateUrl("McpOAuthClients")}/ClientMetadataDomainApproval`;
        return lastValueFrom(this.httpClient.put<IMcpOAuthClient>(url, undefined, { params: { domain } }));
    }

    public deleteMcpOAuthClientMetadataDomainApproval(domain: string) {
        const url = `${this.generateUrl("McpOAuthClients")}/ClientMetadataDomainApproval`;
        return lastValueFrom(this.httpClient.delete<void>(url, { params: { domain } }));
    }

    public deleteMcpOAuthClient(clientId: string) {
        const url = `${this.generateUrl("McpOAuthClients")}/${encodeURIComponent(clientId)}`;
        return lastValueFrom(this.httpClient.delete<void>(url));
    }

    public getMcpOAuthAuthorizations(personId: number) {
        const url = this.generateUrl(`People/${personId}/McpOAuthAuthorizations`);
        return lastValueFrom(this.httpClient.get<IMcpOAuthAuthorization[]>(url));
    }

    public revokeMcpOAuthAuthorization(personId: number, clientId: string) {
        const url = this.generateUrl(`People/${personId}/McpOAuthAuthorizations`);
        return lastValueFrom(this.httpClient.delete<void>(url, { params: { clientId } }));
    }

    public triggerSurveySchedulerWebjob() {
        const url = this.generateUrl("TriggerSurveySchedulerWebjob");
        return this.httpClient.post<string>(url, undefined, { responseType: "json" });
    }

    public triggerEventSchedulerWebjob() {
        const url = this.generateUrl("TriggerEventSchedulerWebjob");
        return this.httpClient.post<string>(url, undefined, { responseType: "json" });
    }

    public triggerStewardshipActivityWebjob() {
        const url = this.generateUrl("TriggerStewardshipActivityWebjob");
        return this.httpClient.post<string>(url, undefined, { responseType: "json" });
    }

    public sendCoachFeedbackLink(coachSessionId: number) {
        const url = this.generateUrl("SendCoachFeedbackLink");
        return this.httpClient.post(url, undefined, {
            responseType: "text",
            params: { coachSessionId: String(coachSessionId) },
        });
    }

    public generateTableSas(tableName: string) {
        return this.generateTableSasCall({ tableName });
    }

    private generateServiceCallFunctionWithUnwrap<T = any>(partialUri: string) {
        const serviceCall = this.generateServiceCallFunction<T>(partialUri);
        return (params: { [key: string]: any } = {}) => {
            return serviceCall(params);
        };
    }

    private generateServiceCallFunction<T = any>(partialUri: string, responseType: "json" | "text" = "json") {
        return (params: { [key: string]: any } = {}) => {
            // TODO: still returning a promise here so that I don't have to go through all usage of this to find missing subscribe and change interfaces
            return lastValueFrom(this.httpClient.get<T>(this.generateUrl(partialUri), { params, responseType: responseType as "json" }));
        };
    }

    private generateUrl(partialUrl: string) {
        return `${ServiceUri.StakeholderServicesServiceBaseUri}/${partialUrl}`;
    }
}
